import React from "react"
import cc from "classcat"
import Highlighter from "react-highlight-words"
import Slider from "react-slick"
import H2 from "components/atoms/H2"

import RequestButton from "components/molecules/RequestButton"
import ContactButton from "components/molecules/ContactButton"
import ScrollButton from "components/molecules/ScrollButton"

const settings = {
  infinite: true,
  slidesToScroll: 1,
  speed: 1000,
  autoplaySpeed: 2500,
  autoplay: true,
  dots: false,
  arrows: false,
  fade: true,
  pauseOnHover: false,
}

export default function Hero({
  Image,
  bgColor,
  slides,
  description,
  searchWords = [],
  flipImage = true,
  buttonType = "request",
  scrollToId,
  buttonLabelKey,
  highlightClassName,
  Button: ButtonOverride,
}: {
  Image: any
  bgColor: any
  slides: any[]
  description: any
  searchWords?: any[]
  flipImage?: boolean
  buttonType?: "request" | "contact" | "scroll"
  scrollToId?: string
  buttonLabelKey?: string
  /** Replaces the underline treatment on the highlighted words. Pass this when
   *  a ground needs the emphasis carried by colour instead of a rule. */
  highlightClassName?: string
  /** Renders in place of the hero's own CTA. */
  Button?: any
}) {
  const slidesData = slides.map((slide) => ({ title: slide }))

  const renderTitle = (item: { title: string }) =>
    item ? (
      <H2
        size="lg"
        className={cc([
          "m-0 space-y-8 tracking-normal sm:whitespace-pre-line ",
          {
            "text-white": !bgColor || bgColor === "black",
            "text-zinc-800": bgColor === "yellow",
          },
        ])}
        theme={bgColor}
      >
        <Highlighter
          highlightClassName={
            highlightClassName ||
            cc([
              "underline bg-transparent underline-offset-8 lg:underline-offset-[12px]",
              {
                "text-zinc-800": !bgColor,
                "text-white": bgColor === "yellow",
                "text-kkm-green": bgColor === "black",
              },
            ])
          }
          searchWords={searchWords || ["prevent", "protect", "impact"]}
          autoEscape
          textToHighlight={item.title}
        />
      </H2>
    ) : null

  let Button
  if (ButtonOverride) {
    Button = ButtonOverride
  } else if (buttonType === "request") {
    Button = RequestButton
  } else if (buttonType === "scroll" && scrollToId) {
    Button = (props) => (
      <ScrollButton
        {...props}
        scrollToId={scrollToId}
        labelKey={buttonLabelKey}
      />
    )
  } else {
    Button = ContactButton
  }
  return (
    <div
      className={cc([
        "relative mt-16 sm:mt-16 lg:mt-20",
        {
          "bg-kkm-green": !bgColor || bgColor === "green",
          "bg-kkm-yellow": bgColor === "yellow",
          "bg-zinc-800": bgColor === "black",
        },
      ])}
    >
      <div className="mx-auto flex max-w-7xl flex-col lg:flex-row">
        <main className="relative mx-auto mt-10 w-full px-4 pb-12 sm:mt-12 sm:px-6 sm:pb-16 md:mt-16 md:pb-20 lg:mx-0 lg:w-1/2 lg:px-8 lg:pb-24 xl:mt-20">
          <div className="space-y-8 sm:text-center lg:space-y-12 lg:text-left">
            {/* A single-slide hero skips react-slick entirely. Slick only
                initialises after hydration, so wrapping one title in a
                carousel left it invisible until the JS ran, which read as the
                headline arriving late. Most heroes on this site pass one
                slide; only the homepage genuinely rotates. */}
            {slidesData.length > 1 ? (
              <Slider slidesToShow={1} {...settings}>
                {slidesData.map((item) => (
                  <div key={item.title}>{renderTitle(item)}</div>
                ))}
              </Slider>
            ) : (
              renderTitle(slidesData[0])
            )}
            <p
              className={cc([
                "m-0 font-display text-lg sm:text-xl lg:text-2xl lg:leading-tight",
                bgColor === "black" ? "text-white" : "text-zinc-800",
              ])}
            >
              <span className="">{description}</span>
            </p>
            <div className="flex max-w-md items-start justify-start sm:mx-auto sm:justify-center lg:mx-0 lg:justify-start">
              <Button bgStyle={bgColor === "black" ? "green" : "black"} left />
            </div>
          </div>
        </main>

        <div className="order-first pl-4 sm:pl-6 lg:absolute lg:inset-y-0 lg:right-0 lg:order-last lg:w-1/2 lg:pl-8 ">
          <div className="relative z-[1] h-full overflow-hidden rounded-l-full">
            <Image
              className={cc([
                "h-56 w-full  sm:h-72 md:h-96 lg:h-full lg:w-full",
                flipImage ? "scale-x-[-1]" : "",
              ])}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
